import { useCallback, useMemo } from 'react';
import { useAPI } from '@/services';
import { useFundEfficiencyCompareContext } from '../../contexts/fundEfficiencyCompareContext';
import { useGetFundHistoryRatio } from './hooks/useGetFundHistoryRatio';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';

export const useGetHistoryComparisonAssetTrendData = () => {
  const fundCode = useGetFundCodeFromQuery();
  const {
    selectedParallelMarket: { symbolName },
  } = useFundEfficiencyCompareContext();

  const {
    data: parallelMarketHistory,
    error: parallelMarketHistoryError,
    isFetching: isFetchingParallelMarketHistory,
    refetch: refetchParallelMarketHistory,
    isError: isErrorParallelMarketHistory,
  } = useAPI(['trade', 'getParallelMarketHistory'], {
    input: {
      OriginSource: fundCode,
      Isin: symbolName,
    },
  });

  const {
    data: fundHistoryRatio,
    error: fundHistoryRatioError,
    isFetching: isFetchingFundHistoryRatio,
    refetch: refetchFundHistoryRatio,
    isError: isErrorFundHistoryRatio,
  } = useGetFundHistoryRatio();

  const isFetching =
    isFetchingParallelMarketHistory || isFetchingFundHistoryRatio;
  const isError = isErrorParallelMarketHistory || isErrorFundHistoryRatio;

  const errorMessage: string | undefined = useMemo(() => {
    if (!isErrorFundHistoryRatio && !isErrorParallelMarketHistory) {
      return undefined;
    }
    return (
      fundHistoryRatioError?.message ||
      parallelMarketHistoryError?.message ||
      ''
    );
  }, [
    parallelMarketHistoryError,
    fundHistoryRatioError,
    isErrorFundHistoryRatio,
    isErrorParallelMarketHistory,
  ]);

  const refetchOnError = useCallback(async () => {
    isErrorFundHistoryRatio && (await refetchFundHistoryRatio());
    isErrorParallelMarketHistory && (await refetchParallelMarketHistory());
  }, [
    isErrorFundHistoryRatio,
    isErrorParallelMarketHistory,
    refetchFundHistoryRatio,
    refetchParallelMarketHistory,
  ]);

  return {
    fundHistoryRatio,
    parallelMarketHistory,
    errorMessage,
    isFetching,
    isError,
    refetchOnError,
  };
};
